import React, { useState, useRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import calendericonnew from "../../assets/calendericon2.svg";
import nocurrentpatient from "../../assets/nocurrentpatient.svg";
import { Modal } from "antd";
import PaymentModeModal from "../PatientRegister/PaymentModeModal";
import { toast } from "react-toastify";
import FullScreenLoader from "../FullScreenLoader";
import { Edit2, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL;
const OCR_API_URL = process.env.REACT_APP_OCR_API_URL;
const OCR_API_KEY = process.env.REACT_APP_OCR_API_KEY;

const getStatusStyle = (status) => {
  switch (status) {
    case "COMPLETED":
      return "text-[#21AA6A] border-[#21AA6A]";
    case "SUCCESS":
      return "text-[#3D2FBA] border-[#3D2FBA]";
    case "QUEUED":
    case "PENDING":
      return " text-cyan-500 border-cyan-500";

    case "INITIATED":
      return "text-[#F29D38] border-[#F29D38]";
    case "ONGOING":
      return "text-cyan-500 border-cyan-500";
    case "CANCELLED":
      return "text-red-500 border-red-500";
    default:
      return "";
  }
};

const CalendarDatePicker = ({ dateRange, setDateRange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingMode, setSelectingMode] = useState("from"); // 'from' or 'until'
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDisplayDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDisplayText = () => {
    const [start, end] = dateRange;
    if (!start && !end) return "Select date range";
    if (start && !end) return formatDisplayDate(start);
    if (start && end) {
      if (start.toDateString() === end.toDateString()) {
        return formatDisplayDate(start);
      }
      return `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`;
    }
    return "Select date range";
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getPreviousMonthDays = (date) => {
    const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 0);
    return prevMonth.getDate();
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  const isInRange = (date) => {
    const [start, end] = dateRange;
    if (!start || !end) return false;
    return date >= start && date <= end;
  };

  const isRangeStart = (date) => {
    return isSameDay(date, dateRange[0]);
  };

  const isRangeEnd = (date) => {
    return isSameDay(date, dateRange[1]);
  };

  const handleDateClick = (date) => {
    const [start, end] = dateRange;

    if (!start || (start && end)) {
      // Start new selection
      setDateRange([date, null]);
      setSelectingMode("until");
    } else if (start && !end) {
      // Complete the range
      if (date >= start) {
        setDateRange([start, date]);
        setSelectingMode("from");
        setIsOpen(false);
      } else {
        setDateRange([date, start]);
        setSelectingMode("from");
        setIsOpen(false);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
    const prevMonthDays = getPreviousMonthDays(currentMonth);

    const days = [];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Previous month days (grayed out)
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() - 1,
        day
      );
      days.push(
        <button
          key={`prev-${day}`}
          onClick={() => handleDateClick(date)}
          className="w-10 h-10 text-gray-300 hover:bg-gray-100 rounded-full text-sm"
        >
          {day}
        </button>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      const isStart = isRangeStart(date);
      const isEnd = isRangeEnd(date);
      const inRange = isInRange(date);
      const isToday = isSameDay(date, new Date());

      let className =
        "w-10 h-10 text-gray-700 hover:bg-green-100 rounded-full text-sm relative ";

      if (isStart || isEnd) {
        className += "bg-green-400 text-white hover:bg-green-500 ";
      } else if (inRange) {
        className += "bg-green-200 text-green-800 ";
      } else if (isToday) {
        className += "border border-green-600 ";
      }

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(date)}
          className={className}
        >
          {day}
        </button>
      );
    }

    // Next month days (grayed out)
    const totalCells = 42; // 6 rows × 7 days
    const remainingCells = totalCells - days.length;
    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        day
      );
      days.push(
        <button
          key={`next-${day}`}
          onClick={() => handleDateClick(date)}
          className="w-10 h-10 text-gray-300 hover:bg-gray-100 rounded-full text-sm"
        >
          {day}
        </button>
      );
    }

    return (
      <div className="p-4">
        {/* Header with month/year and navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-lg font-medium">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </div>
          <button
            onClick={() => navigateMonth(1)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* From/Until tabs */}
        <div className="flex mb-4 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setSelectingMode("from")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectingMode === "from"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            From
          </button>
          <button
            onClick={() => setSelectingMode("until")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectingMode === "until"
                ? "bg-green-400 text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Until
          </button>
        </div>

        {/* Day names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div
              key={day}
              className="w-10 h-8 flex items-center justify-center text-xs font-medium text-gray-500"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors min-w-[200px] text-left"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span className="flex-1 text-sm text-gray-700">{getDisplayText()}</span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-80">
          {renderCalendar()}
        </div>
      )}
    </div>
  );
};

export default function TodayAppointments({
  appointments = [],
  dateRange,
  setDateRange,
  paidStatus,
  setPaidStatus,
  refetchAppointments,
}) {
  const [docModalVisible, setDocModalVisible] = useState(false);
  const [docUrl, setDocUrl] = useState("");
  const [isPdf, setIsPdf] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPaymentItem, setSelectedPaymentItem] = useState(null);
  const [paymentMode, setPaymentMode] = useState("");

  const [loadingOcr, setLoadingOcr] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewImageURL, setPreviewImageURL] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [mainData, setMainData] = useState({
    patientDrugPrescriptionList: [],
    patientLabTestsList: [],
    recommendation: "",
    symptoms: [],
    followUpVisitDate: null,
    diagnosis: null,
    surgeryRecommendation: "",
  });

  console.log("Lab Tests:", mainData.patientLabTestsList);
  const hiddenFileInput = useRef([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMedicineIdx, setEditingMedicineIdx] = useState(null);
  const [newMedicine, setNewMedicine] = useState({
    name: "",
    composition: "",
    quantity: 1,
    dosage: "",
    instructions: "",
    duration: "",
    medicineTypeOfSell: "",
    medicineRate: "",
    drugCategory: "",
    route: "",
    drugCode: "",
     prescriptionRequired:""
  });
  const [showAddLabModal, setShowAddLabModal] = useState(false);
  const [editingLabIdx, setEditingLabIdx] = useState(null);
  const [manualLabTest, setManualLabTest] = useState("");
  const [selectedLabDetails, setSelectedLabDetails] = useState({
    code: "",
    amount: "",
  });
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [testOptions, setTestOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchResultsdata, setSearchResultsdata] = useState([]);
  const [isUserTyping, setIsUserTyping] = useState(false);

  const navigate = useNavigate();

  React.useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (isUserTyping && newMedicine.name.trim().length >= 3) {
        fetchMedicine();
      } else {
        setSearchResultsdata([]);
      }
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [newMedicine.name, isUserTyping]);

  const fetchMedicine = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_ES_URL}medicine?freeTextSearch=${newMedicine.name}&pageNo=1&pageSize=20`
      );
      const result = await response.json();
      setLoading(false);
      if (result && result.length > 0) {
        setSearchResultsdata(result);
      } else {
        setSearchResultsdata([]);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    setNewMedicine({
      ...newMedicine,
      name: item.medicineName || "",
      composition: item.composition || "",
      medicineTypeOfSell: item.medicineTypeOfSell || "",
      medicineRate: item.medicineRate || "",
      drugCategory: item.drugCategory || "",
      route: item.route || "",
      drugCode: item.drugCode || "",
       prescriptionRequired:item.prescriptionRequired ||""
    });
    setSearchResultsdata([]);
    setIsUserTyping(false);
  };

  const handleDeleteMedicine = (idx) => {
    setMainData((prev) => ({
      ...prev,
      patientDrugPrescriptionList: prev.patientDrugPrescriptionList.filter(
        (_, i) => i !== idx
      ),
    }));
  };
  const handleEditMedicine = (idx) => {
    const med = mainData.patientDrugPrescriptionList[idx];
    setNewMedicine({
      name: med.drugName,
      composition: med.composition,
      quantity: med.quantity,
      dosage: med.dosage || "",
      instructions:
        med.instruction === "1"
          ? "Before Meal"
          : med.instruction === "0"
          ? "After Meal"
          : "",
      duration: med.duration || "",
      route: med.route || "",
      medicineTypeOfSell: med.medicineTypeOfSell || "",
      medicineRate: med?.medicineRate || "",
      drugCategory: med?.drugCategory || "",
      drugCode: med?.id || "",
       prescriptionRequired:med?.prescriptionRequired ||""
    });
    setEditingMedicineIdx(idx);
    setShowAddModal(true);
  };
  const handleSaveMedicine = () => {
    if (editingMedicineIdx !== null) {
      setMainData((prev) => {
        const meds = [...prev.patientDrugPrescriptionList];
        meds[editingMedicineIdx] = {
          ...meds[editingMedicineIdx],
          drugName: newMedicine.name,
          composition: newMedicine.composition,
          quantity: newMedicine.quantity,
          dosage: newMedicine.dosage,
          instruction:
            newMedicine.instructions === "Before Meal"
              ? "1"
              : newMedicine.instructions === "After Meal"
              ? "0"
              : "",
          duration: newMedicine.duration,
          route: newMedicine.route,
          medicineTypeOfSell: newMedicine.medicineTypeOfSell,
          medicineRate: newMedicine.medicineRate,
          drugCategory: newMedicine.drugCategory,
          drugCode: newMedicine.drugCode,
          prescriptionRequired:newMedicine.prescriptionRequired,
        };
        return { ...prev, patientDrugPrescriptionList: meds };
      });
    } else {
      setMainData((prev) => ({
        ...prev,
        patientDrugPrescriptionList: [
          ...prev.patientDrugPrescriptionList,
          {
            drugName: newMedicine.name,
            composition: newMedicine.composition,
            quantity: newMedicine.quantity,
            dosage: newMedicine.dosage,
            instruction:
              newMedicine.instructions === "Before Meal"
                ? "1"
                : newMedicine.instructions === "After Meal"
                ? "0"
                : "",
            duration: newMedicine.duration,
            route: newMedicine.route,
            medicineTypeOfSell: newMedicine.medicineTypeOfSell,
            medicineRate: newMedicine.medicineRate,
            drugCategory: newMedicine.drugCategory,
            drugCode: newMedicine.drugCode,
              prescriptionRequired:newMedicine.prescriptionRequired,
          },
        ],
      }));
    }
    setNewMedicine({
      name: "",
      composition: "",
      quantity: 1,
      dosage: "",
      instructions: "",
      duration: "",
      medicineTypeOfSell: "",
      medicineRate: "",
      drugCategory: "",
      route: "",
      drugCode: "",
      prescriptionRequired:""
    });
    setEditingMedicineIdx(null);
    setShowAddModal(false);
  };

  const dosageOptions = [
    { label: "Select", value: "" },
    { label: "0-0-1", value: "0-0-1" },
    { label: "0-1-1", value: "0-1-1" },
    { label: "0-1-0", value: "0-1-0" },
    { label: "1-1-1", value: "1-1-1" },
    { label: "1-0-0", value: "1-0-0" },
    { label: "1-0-1", value: "1-0-1" },
    { label: "1-1-0", value: "1-1-0" },
    { label: "1-1-1-1", value: "1-1-1-1" },
    { label: "1-0-0-0", value: "1-0-0-0" },
    { label: "1-1-0-0", value: "1-1-0-0" },
    { label: "1-1-1-0", value: "1-1-1-0" },
    { label: "0-0-0-1", value: "0-0-0-1" },
    { label: "0-0-1-1", value: "0-0-1-1" },
    { label: "0-0-1-0", value: "0-0-1-0" },
    { label: "0-1-0-0", value: "0-1-0-0" },
    { label: "0-1-1-1", value: "0-1-1-1" },
    { label: "s.o.s.", value: "s.o.s." },
    { label: "q.wk.", value: "q.wk." },
    { label: "b.i.d.", value: "b.i.d." },
    { label: "t.i.d.", value: "t.i.d." },
    { label: "q.i.d.", value: "q.i.d." },
    { label: "q.h.s.", value: "q.h.s." },
    { label: "5X a day", value: "5X a day" },
    { label: "q.4h", value: "q.4h" },
    { label: "q.6h", value: "q.6h" },
    { label: "q.o.d.", value: "q.o.d." },
    { label: "prn.", value: "prn." },
    { label: "q.d.", value: "q.d." },
    { label: "I.M.", value: "I.M." },
    { label: "Subq.", value: "Subq." },
    { label: "Rectally", value: "Rectally" },
    { label: "I.V.", value: "I.V." },
    { label: "O.D.", value: "O.D." },
    { label: "O.S.", value: "O.S." },
    { label: "O.U.", value: "O.U." },
    { label: "2 ml", value: "2 ml" },
    { label: "2.5 ml", value: "2.5 ml" },
    { label: "5 ml", value: "5 ml" },
    { label: "10 ml", value: "10 ml" },
  ];

  const handleDeleteLab = (idx) => {
    setMainData((prev) => ({
      ...prev,
      patientLabTestsList: prev.patientLabTestsList.filter((_, i) => i !== idx),
    }));
  };
  const handleEditLab = async (idx) => {
    const lab = mainData.patientLabTestsList[idx];
    setManualLabTest(lab.labTestName);
    setEditingLabIdx(idx);
    setShowAddLabModal(true);

    // Fetch latest details from API for this test name
    const details = await fetchLabTestDetails(lab.labTestName);
    setSelectedLabDetails({
      amount: details.amount || "",
      code: details.labTestId || "",
    });
  };
  const handleSaveLab = () => {
    setMainData((prev) => {
      const labs = [...prev.patientLabTestsList];
      if (editingLabIdx !== null) {
        labs[editingLabIdx] = {
          ...labs[editingLabIdx],
          labTestName: manualLabTest,
          labTestId: selectedLabDetails.code,
          amount: selectedLabDetails.amount,
        };
      } else {
        labs.push({
          labTestName: manualLabTest,
          amount: selectedLabDetails.amount || "",
          status: 1,
        });
      }
      return { ...prev, patientLabTestsList: labs };
    });
    setManualLabTest("");
    setEditingLabIdx(null);
    setShowAddLabModal(false);
  };
  const searchTests = async (event) => {
    const query = event.target.value;
    setManualLabTest(query);

    if (query.trim().length < 2) {
      setFilteredOptions([]);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
      `${
          process.env.REACT_APP_DIAGNOSISURL
        }partner-labs?labCategory=LAB&freeTextSearch=${encodeURIComponent(
          query
        )}`
      );
      const data = await response.json();
      console.log("Lab search API result:", data); // <-- Add this line
      if (Array.isArray(data)) {
        setFilteredOptions(
          data.map((test) => ({
            label: test.displayName || test.name,
            value: test.displayName || test.name,
            code: test.Curebaytestcode || test.Curebaytestcode,
            amount: test.listPrice || "",
            labType: test.labType || "",
          }))
        );
      } else {
        setFilteredOptions([]);
      }
    } catch (error) {
      setFilteredOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAndShowDocument = async (fileName) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE}patient-medical-history/file-url?fileName=${fileName}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const result = await response.json();
      if (result.signedUrl) {
        setDocUrl(result.signedUrl);
        setIsPdf(fileName.toLowerCase().endsWith(".pdf"));
        setDocModalVisible(true);
      } else {
        alert("Failed to retrieve document URL");
      }
    } catch (err) {
      alert("Failed to fetch document URL");
    }
  };

  const handlePay = (appointmentItem) => {
    setSelectedPaymentItem(appointmentItem);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    refetchAppointments();
  };

  // Replace the handler to use appointmentId as the key
  const handleUploadClick = (appointmentId) => {
    setSelectedAppointmentId(appointmentId);
    if (hiddenFileInput.current && hiddenFileInput.current[appointmentId]) {
      hiddenFileInput.current[appointmentId].value = null; // reset file input
      hiddenFileInput.current[appointmentId].click();
    }
  };

  // const fetchLabTestDetails = async (labTestName) => {
  //   const token = localStorage.getItem("auth_token");

  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL_DOCTOR}DiagnosticsLabTests/list/filter?parentId=0&status=1`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );

  //     const data = await response.json();

  //     if (Array.isArray(data)) {
  //       const match = data.find((test) =>
  //         test?.name?.toLowerCase().includes(labTestName.toLowerCase())
  //       );

  //       return {
  //         labTestName: match?.name || labTestName,
  //         labTestId: match?.code || "",
  //         status: 1,
  //         amount: match?.amount || "",
  //         discountedAmount: "",
  //       };
  //     }
  //   } catch (err) {
  //     console.error(`Error fetching lab test for ${labTestName}:`, err);
  //   }

  //   return {
  //     labTestName: labTestName,
  //     labTestId: "",
  //     status: 1,
  //     amount: "",
  //     discountedAmount: "",
  //   };
  // };

  const fetchLabTestDetails = async (labTestName) => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_DIAGNOSISURL
        }partner-labs?labCategory=LAB&freeTextSearch=${encodeURIComponent(
          labTestName
        )}`,
        {}
      );

      const data = await response.json();
      console.log(data, "Lab Test Data");

      // Handle the response based on the new API structure
      if (data && Array.isArray(data)) {
        const match = data?.find((test) =>
          test?.displayName?.toLowerCase().includes(labTestName.toLowerCase())
        );
        console.log(match, "match");

        return {
          labTestName: match?.displayName || labTestName,
          labTestId: match?.Curebaytestcode || match?.Curebaytestcode || "",
          status: 1,
          amount: match?.listPrice || match?.listPrice || "",
          labType: match?.labType || "",
        };
      } else if (Array.isArray(data)) {
        // Fallback in case the response is directly an array
        const match = data.find((test) =>
          test?.name?.toLowerCase().includes(labTestName.toLowerCase())
        );

        return {
          labTestName: match?.displayName || labTestName,
          labTestId: match?.Curebaytestcode || match?.Curebaytestcode || "",
          status: 1,
          amount: match?.listPrice || match?.listPrice || "",
          labType: match?.labType || "",
        };
      }
    } catch (err) {
      console.error(`Error fetching lab test for ${labTestName}:`, err);
    }

    return {
      labTestName: labTestName,
      labTestId: "",
      status: 1,
      amount: "",
      labType: "",
    };
  };

  const handlePrescriptionUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewImageURL(url);
    const formData = new FormData();
    formData.append("file", file);
    try {
      setLoadingOcr(true);
      const response = await fetch(
        `${OCR_API_URL}medicine_prescription/process`,
        {
          method: "POST",
          headers: {
            "X-API-Key": OCR_API_KEY,
          },
          body: formData,
        }
      );
      const data = await response.json();
      if (data?.detail) {
        toast.error(data.detail);
        setLoadingOcr(false);
        return;
      }
      // Helper function to fetch composition and drugCode
      const fetchMedicineDetails = async (drugName) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_ES_URL}medicine?freeTextSearch=${drugName}&pageNo=1&pageSize=20`
          );
          const result = await response.json();
          if (Array.isArray(result) && result.length > 0) {
            const match = result[0];
            return {
              composition: match.composition || "",
              drugCode: match.id || "",
              medicineRate: match.medicineRate || "",
              medicineTypeOfSell: match.medicineTypeOfSell || "",
              drugCategory: match.drugCategory || "",
               prescriptionRequired:match.prescriptionRequired ||""
            };
          }
        } catch (error) {
          console.error(`Error fetching for ${drugName}:`, error);
        }
        return {
          composition: "",
          drugCode: "",
        };
      };
      // Enhance each medicine with composition and drugCode
      const medicineDetailsRaw = data?.medicine_details || [];
      const enrichedMedicines = await Promise.all(
        medicineDetailsRaw.map(async (item) => {
          const {
            composition,
            drugCode,
            medicineRate,
            medicineTypeOfSell,
            drugCategory,
            prescriptionRequired
          } = await fetchMedicineDetails(item?.name || "");
          return {
            medicineRate: medicineRate,
            medicineTypeOfSell: medicineTypeOfSell,
            drugCategory: drugCategory,
            drugName: item?.name,
            composition: composition,
            drugCode: drugCode,
            dosage: item?.dosage,
            prescriptionRequired:prescriptionRequired,
            instruction: item?.instructions?.toLowerCase().includes("before")
              ? "1"
              : item?.instructions?.toLowerCase().includes("after")
              ? "0"
              : "2",
            duration: item?.duration,
            notes: item?.notes,
            quantity: item?.quantity,
            drugMType: "Medicine",
            afterBeforeFood: "",
            status: 1,
          };
        })
      );
      // Lab test details (minimal for now)
      const labTestList = await Promise.all(
        (data?.lab_test_details?.tests || []).map((test) =>
          fetchLabTestDetails(test)
        )
      );
      console.log("Lab Test List:", labTestList);
      // Follow up date - use appointment date as base
      const selectedAppointmentObject = appointments.find(
        (item) => item.appointment.appointmentId === selectedAppointmentId
      );
      const appointmentDate = selectedAppointmentObject?.appointment?.whenAppointment || new Date().toISOString().split("T")[0];
      
      console.log("Appointment Date:", appointmentDate);
      console.log("Selected Appointment Object:", selectedAppointmentObject);
      
      const getFormattedDate = (daysToAdd) => {
        const date = new Date(appointmentDate);
        console.log("Base date for calculation:", date);
        date.setDate(date.getDate() + daysToAdd);
        const result = date.toISOString().split("T")[0];
        console.log(`Follow-up date (${daysToAdd} days):`, result);
        return result;
      };
      let followUpDate = null;
      if (data?.schedule_followup) {
        console.log("Schedule followup data:", data.schedule_followup);
        if (data.schedule_followup.after_1_week) {
          followUpDate = getFormattedDate(7);
        } else if (data.schedule_followup.after_15_days) {
          followUpDate = getFormattedDate(15);
        } else if (data.schedule_followup.after_1_month) {
          followUpDate = getFormattedDate(30);
        }
      }
      console.log("Final calculated followUpDate:", followUpDate);
      setMainData({
        patientDrugPrescriptionList: enrichedMedicines,
        patientLabTestsList: labTestList,
        recommendation: data?.recommendation || "",
        symptoms: data?.symptoms || [],
        followUpVisitDate: followUpDate,
        diagnosis: data?.diagnosis,
        surgeryRecommendation: data?.surgery_recommendation || data?.surgeryRecommendation || "",
      });
      setPreviewModalOpen(true);
    } catch (err) {
      toast.error("Failed to scan prescription.");
    } finally {
      setLoadingOcr(false);
    }
  };

 

  const uploadToServer = async () => {
     setUploading(true);
     try{
    const token = localStorage.getItem("auth_token");
    const tenantId = localStorage.getItem("tenantId");
  
      const selectedAppointmentObject = appointments.find(
        (item) => item.appointment.appointmentId === selectedAppointmentId
      );
      const selectedAppointment = selectedAppointmentObject.appointment;
      const abhaNumber = selectedAppointmentObject.abhaNumber; // Get abhaNumber from the root level
      const doctorId = selectedAppointmentObject.doctorId; // Get doctorId from the root level
      const doctorName = selectedAppointmentObject.doctorName; // Get doctorName from the root level
      const preferredAbhaAddress =
        selectedAppointmentObject.preferredAbhaAddress; // Get preferredAbhaAddress from the root level
      const patientName =
        selectedAppointment.patientName || selectedAppointment.patientId;

      const visitDate =
        selectedAppointment?.whenAppointment ||
        new Date().toISOString().split("T")[0];
      // Sanitize medicine data
      const cleanedDrugList = (mainData.patientDrugPrescriptionList || []).map(
        (med) => ({
          duration: isNaN(Number(med.duration)) ? "" : med.duration,
          quantity: isNaN(Number(med.quantity)) ? "" : med.quantity,
          rate: isNaN(Number(med.medicineRate)) ? "" : med.medicineRate,
          drugName: med.drugName,
          composition: med.composition,
          drugCode: med.drugCode,
          dosage: med.dosage,
          instruction: med.instruction,
          notes: med.notes,
          typeOfSell: med.medicineTypeOfSell,
          drugCategory: med.drugCategory,
          drugMType: "Medicine",
          status: 1,
          prescriptionRequired:med.prescriptionRequired
        })
      );
      // Construct JSON payload
      const jsonPayload = {
        visitId: "",
        appointmentId: selectedAppointmentId,
        patientName: patientName,
        patientMobileNo: selectedAppointment?.patientMobileNo || "",
        complaints: selectedAppointment?.consultationsReason || "",
        consultReason: selectedAppointment?.consultationsReason || "",
        consultationType: selectedAppointment?.consultationType || "V",
        userId: selectedAppointment?.userId || "",
        diagnostic: "",
        documentType: "e-prescription",
        entryType: "V",
        patientDocument: "",
        followUpVisitDate: mainData.followUpVisitDate ? new Date(mainData.followUpVisitDate).toISOString().split('T')[0] : "",
        primaryDiagnosis: mainData.diagnosis?.primary || "",
        primarySymptoms: "",
        recommendation: mainData.recommendation || "",
        roleCode: "",
        saveStatus: "Completed",
        secondaryDiagnosis: mainData.diagnosis?.secondary || "",
        secondarySymptoms: "",
        serviceProvided: "",
        status: 1,
        patientId: selectedAppointment?.patientId || "",
        symptomsList: mainData.symptoms || [],
        onset: "",
        bodyPartLocation: "",
        characteristics: "",
        aggravatingFactors: "",
        relievingFactors: "",
        treatmentTaken: "",
        adverseEffect: "",
        severity: "",
        frame: "",
        coating: "",
        nvAssessment: "",
        dvAssessment: "",
        treatmentOption: "",
        surgeryRecommendation: mainData.surgeryRecommendation || "",
        visitDate: visitDate,
        visitSummary: "",
        visitType: selectedAppointment?.consultationType || "V",
        comments: "",
        linkType: "e-prescription",
        patientDrugPrescriptionList: cleanedDrugList,
        patientLabTestsList: mainData.patientLabTestsList || [],
        eyeReadingMain: {},
        url: "",
        clinicId: selectedAppointment?.clinicId || "",
      };
      
      // Log the calculated follow-up date for debugging
      console.log("Calculated Follow-up Date being sent in payload:", mainData.followUpVisitDate);
      console.log("Full payload followUpVisitDate field:", jsonPayload.followUpVisitDate);
      
      // Construct form data
      const formData = new FormData();
      formData.append("prescription", JSON.stringify(jsonPayload));
      formData.append("file", uploadedFile);
      const saveResponse = await fetch(`${API_BASE}prescriptions/save`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
        },
        body: formData,
      });
      const saveResult = await saveResponse.json();
      if (saveResult.message) {
        toast.success(
          saveResult.message || "Patient prescription saved successfully"
        );

        // Only call ABDM data share API if abhaNumber is present
        console.log("Checking for abhaNumber:", abhaNumber);
        if (abhaNumber) {
          console.log("ABDM API call starting - abhaNumber found:", abhaNumber);
          // Fetch clinic details for ABDM data share
          try {
            const clinicResponse = await fetch(`${API_BASE}clinicDetails`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            const clinicData = await clinicResponse.json();

            // Format visit date for ABDM (DD-MM-YYYY)
            const formattedVisitDate = new Date(visitDate)
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
              .split("/")
              .join("-");

            // Transform prescription data for ABDM format
            const abdmPrescriptions = (
              mainData.patientDrugPrescriptionList || []
            ).map((med) => {
              // Calculate frequency by counting '1's in dosage string
              const dosageString = med.dosage || "";
              const frequency = (dosageString.match(/1/g) || []).length;

              return {
                drugName: med.drugName || "",
                dosage: med.composition || "",
                frequency: frequency.toString(),
                route: med.route || "Oral", // Use actual route value from medicine data
                duration: med.duration || "",
                instructions: med.notes || "",
                prescriptionDate: "",
                method:
                  med.instruction === "1"
                    ? "Before Meal"
                    : med.instruction === "0"
                    ? "After Meal"
                    : "",
              };
            });

            // Prepare ABDM payload
            const abdmPayload = {
              organizationDetails: {
                name: clinicData.name || "CureBay eClinic BBSR 2",
                address:
                  clinicData.address ||
                  "Plot No: 5208/5840, Ground Floor, At - Sachivalaya Marg, Sainik School Rd, near Zaika Palace, Gajapati Nagar",
                mobile: clinicData.mobile || "9437045663",
                hipId: "IN2110000775", // Default HIP ID
              },
              healthcareProfessionalId:
                doctorId ||
                selectedAppointment?.userId ||
                "6848f3092d1331685af80369",
              visitDate: formattedVisitDate,
              abhaNumber: abhaNumber,
              abhaAddress: preferredAbhaAddress || "",

              documentType: "Prescription",
              physicianInfo: {
                name: doctorName || "Dr Rakesh Dash",
                prefix: "",
                gender: "",
                qualification: "",
                contact_info: {
                  phone: "",
                },
              },
              prescriptionsDetails: abdmPrescriptions,
              encounterType: "Outpatient Visit",
              encounterCode: "AMB",
              patientId: "",
            };

            // Call ABDM data share API
            const abdmResponse = await fetch(
              `${process.env.REACT_APP_API_URL_V2}abdm-data-share/save-document`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJDVVJFQkFZIiwicm9sZSI6IkFETUlOIiwiaWF0IjoxNzU0MzA0NzQyLCJleHAiOjE3NTQzNDc5NDJ9._EySjnDzpg8UT7I4uy3D3DCUZUB5jwuwmc4yGP4qJd-Uu5DPYhY6QtejqFTfSNozEWrHisGXN7DIKr_CuhNR_w`,
                },
                body: JSON.stringify(abdmPayload),
              }
            );

            const abdmResult = await abdmResponse.json();
            if (abdmResult.success || abdmResult.message) {
              console.log("ABDM data share successful:", abdmResult);
            } else {
              console.warn("ABDM data share failed:", abdmResult);
            }
          } catch (abdmError) {
            console.error("Error in ABDM data share:", abdmError);
            // Don't show error to user as this is secondary functionality
          }
        } else {
          console.log("ABDM API call skipped - no abhaNumber found");
        }

        refetchAppointments();
      } else {
        toast.error(saveResult.error || "Patient prescription failed to save");
      }
      setPreviewModalOpen(false);
    } catch (err) {
      toast.error("Failed to upload or save prescription.");
    } finally {
      setUploading(false);
    }
  };

  // Filter appointments based on paidStatus
  const filteredAppointments = React.useMemo(() => {
    if (paidStatus === "ALL") {
      return appointments;
    } else if (paidStatus === "PAID") {
      return appointments.filter(
        (item) => item.appointment.paymentStatus === "SUCCESS"
      );
    } else if (paidStatus === "UNPAID") {
      // For UNPAID, show only QUEUED status appointments with INITIATED payment
      return appointments.filter(
        (item) =>
          item.appointment.paymentStatus === "INITIATED" &&
          item.appointment.status === "QUEUED"
      );
    }
    return appointments;
  }, [appointments, paidStatus]);

  return (
    <>
      {loadingOcr && <FullScreenLoader message="Scanning Prescription..." />}

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-[#004276]">
          Appointments{" "}
          <span className="bg-[#00BD7F] text-white font-medium px-2 py-1 rounded-md text-sm ml-1">
            {filteredAppointments.length}
          </span>
        </h2>
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <CalendarDatePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          </div>
          <select
            value={paidStatus}
            onChange={(e) => setPaidStatus(e.target.value)}
            className="w-[146px] h-[35px] px-[0.4rem] py-2 text-sm text-gray-500 border-[0.5px] border-[#C6C6C6] rounded-md focus:outline-none"
          >
            <option value="ALL">All</option>
            <option value="PAID">Paid</option>
            <option value="UNPAID">Unpaid</option>
          </select>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-[0px_4px_8px_rgba(0,0,0,0.15)]">
        {filteredAppointments.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-10 space-y-4">
            <img
              src={nocurrentpatient}
              alt="No appointments"
              className="h-40 w-40"
            />
            <p className="text-gray-500">
              {paidStatus === "UNPAID"
                ? "No unpaid appointments with QUEUED status found."
                : "No appointments scheduled for today."}
            </p>
          </div>
        ) : (
          <div className="max-h-[27rem] overflow-y-auto">
            {filteredAppointments.map((item, index) => {
              const appt = item.appointment;
              const isPaid = appt.paymentStatus === "SUCCESS";
              const isCompleted = appt.status === "COMPLETED";
              const patientName =
                item.patientName || appt.patientName || appt.patientId;

              return (
                <div
                  key={appt.appointmentId}
                  className={`flex items-center justify-between px-6 py-4 border-b ${
                    index === filteredAppointments.length - 1
                      ? "border-none"
                      : ""
                  }`}
                >
                  {/* Left Section */}
                  <div className="flex gap-4 items-start">
                    <div className="h-8 w-8 border border-[#004276] rounded-full flex items-center justify-center text-[#004276] font-bold text-sm">
                      {patientName ? patientName.charAt(0) : "P"}
                    </div>
                    <div>
                      <p className="text-[#004276] font-semibold text-base mb-1">
                        {patientName}
                      </p>
                      <p className="text-sm text-[#6B7280] mb-3">
                        Token No:{" "}
                        <span className="font-bold text-black">
                          {appt.tokenNumber}
                        </span>{" "}
                        | Phone No:{" "}
                        <span className="font-bold text-black">
                          {appt.patientMobileNo || "N/A"}
                        </span>{" "}
                        | Doctor:{" "}
                        <span className="font-bold text-black">
                         {item.doctorName ? "Dr.":""} {item.doctorName || "N/A"}
                        </span>{" "}
                        Symptom:{" "}
                        <span className="font-bold text-black">
                          {appt.consultationsReason}
                        </span>
                      </p>

                      {/* Conditional Buttons */}
                      <div className="flex gap-3">
                        {!isPaid && (
                          <button
                            onClick={() => handlePay(item)}
                            className="bg-[#004276] font-semibold text-white px-10 py-2 rounded-full text-sm"
                          >
                            Pay Now
                          </button>
                        )}
                        {isCompleted &&
                          (item.prescriptionDoc ? (
                            <>
                              <button
                                className="bg-[#004276] font-semibold text-white px-5 py-2 rounded-full text-sm"
                                onClick={() =>
                                  navigate("/BookService", {
                                    state: {
                                      patientCode: appt.patientId,
                                      patientName: appt.patientName,
                                      whenAppointment: appt.whenAppointment,
                                    },
                                  })
                                }
                              >
                                Schedule a Service
                              </button>
                              <button
                                onClick={() =>
                                  fetchAndShowDocument(item.prescriptionDoc)
                                }
                                className="border border-[#004276] text-[#004276] font-semibold px-5 py-2 rounded-full text-sm"
                              >
                                View Prescription
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() =>
                                  handleUploadClick(appt.appointmentId)
                                }
                                className="border border-[#004276] text-[#004276] px-6 py-2 rounded-full text-sm"
                              >
                                Upload Prescription
                              </button>
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                ref={(el) =>
                                  (hiddenFileInput.current[appt.appointmentId] =
                                    el)
                                }
                                onChange={handlePrescriptionUpload}
                                className="hidden"
                              />
                            </>
                          ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Section */}
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-1 text-sm text-gray-600 font-semibold">
                      <img
                        src={calendericonnew}
                        alt="calendar"
                        className="w-4 h-4"
                      />
                      {appt.whenAppointment}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <span
                        className={`text-xs font-semibold border px-2 py-1 rounded-md ${getStatusStyle(
                          appt.status
                        )}`}
                      >
                        {appt.status}
                      </span>
                      <span
                        className={`text-xs font-semibold border px-2 py-1 rounded-md ${getStatusStyle(
                          appt.paymentStatus
                        )}`}
                      >
                        {appt.paymentStatus === "INITIATED"
                          ? "UNPAID"
                          : appt.paymentStatus === "SUCCESS"
                          ? "PAID"
                          : appt.paymentStatus}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Modal
        open={docModalVisible}
        onCancel={() => setDocModalVisible(false)}
        width={800}
        footer={
          <div className="flex justify-end gap-4">
            <button
              className="px-4 py-2 bg-[#004276] text-white rounded"
              onClick={() => {
                const link = document.createElement("a");
                link.href = docUrl;
                link.download = docUrl.split("/").pop();
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download
            </button>
            <button
              onClick={() => setDocModalVisible(false)}
              className="px-4 py-2 border border-gray-400 text-gray-600 rounded hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        }
      >
        {isPdf ? (
          <iframe
            src={docUrl}
            title="PDF Preview"
            className="w-full h-[80vh]"
            frameBorder="0"
          />
        ) : (
          <img
            src={docUrl}
            alt="Document Preview"
            className="w-full max-h-[80vh] object-contain"
          />
        )}
      </Modal>
      {modalOpen && (
        <PaymentModeModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          onPay={handlePay}
          setPaymentMode={setPaymentMode}
          isLoggingIn={false}
          paymentMode={paymentMode}
          paymentItem={selectedPaymentItem?.appointment?.id}
        />
      )}
      {previewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-40 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[98vw] sm:max-w-[80rem] p-2 sm:p-8">
            <h3 className="text-2xl font-bold text-[#003A71] mb-6">
              Prescription Preview & OCR Results
            </h3>
            <div className="grid gap-8 grid-cols-1 xl:grid-cols-[28rem_1fr]">
              {/* LEFT: Preview */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {uploadedFile?.type === "application/pdf" ? (
                  <embed
                    src={previewImageURL}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    className="h-[60vh] w-full"
                  />
                ) : (
                  <img
                    src={previewImageURL}
                    alt="Preview"
                    className="object-contain h-[60vh] w-full"
                  />
                )}
              </div>
              {/* RIGHT: OCR Results */}
              <div className="max-h-[60vh] overflow-y-auto pr-2">
                {/* Medicine Details */}
                <h4 className="text-lg font-semibold text-[#003A71] mb-2">
                  Medicine Details
                </h4>
                <button
                  onClick={() => {
                    setEditingMedicineIdx(null);
                    setShowAddModal(true);
                  }}
                  className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded mb-4"
                >
                  + Add Medicine
                </button>
                <table className="w-full text-sm border-collapse mb-6">
                  <thead className="bg-[#F0FCFA] sticky top-0">
                    <tr>
                      {[
                        "Name",
                        "Dosage",
                        "Instructions",
                        "Duration",
                        "Qty",
                        "Amount",
                        "Type",
                        "Route",
                        "Edit",
                        "Delete",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left text-[#003A71] font-medium"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mainData.patientDrugPrescriptionList.map((med, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{med.drugName}</td>
                        <td className="px-3 py-2">{med.dosage || "—"}</td>
                        <td className="px-3 py-2">
                          {med.instruction === "0"
                            ? "After Meal"
                            : med.instruction === "1"
                            ? "Before Meal"
                            : ""}
                        </td>
                        <td className="px-3 py-2">{med.duration || "—"}</td>
                        <td className="px-3 py-2">{med.quantity || "—"}</td>
                        <td className="px-3 py-2">{med.medicineRate || "—"}</td>
                        <td className="px-3 py-2">
                          {med.medicineTypeOfSell || "—"}
                        </td>
                        <td className="px-3 py-2">{med.route || "—"}</td>
                        <td className="px-3 py-2">
                          <button onClick={() => handleEditMedicine(i)}>
                            <Edit2 className="w-5 h-5 text-[#004276] hover:opacity-75" />
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => handleDeleteMedicine(i)}>
                            <Trash2 className="w-5 h-5 text-red-500 hover:opacity-75" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Lab-Test Details */}
                {mainData.patientLabTestsList.length > 0 && (
                  <>
                    <h4 className="text-lg font-semibold text-[#003A71] mb-2">
                      Lab Test Details
                    </h4>
                    <button
                      onClick={() => {
                        setEditingLabIdx(null);
                        setShowAddLabModal(true);
                      }}
                      className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded mb-4"
                    >
                      + Add Lab Test
                    </button>
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-[#F0FCFA] sticky top-0">
                        <tr>
                          {["Test Name", "Amount", "Edit", "Delete"].map(
                            (h) => (
                              <th
                                key={h}
                                className="px-3 py-2 text-left text-[#003A71] font-medium"
                              >
                                {h}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {mainData.patientLabTestsList.map((test, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2">{test.labTestName}</td>
                            <td className="px-3 py-2">{test.amount || "—"}</td>
                            <td className="px-3 py-2">
                              <button onClick={() => handleEditLab(i)}>
                                <Edit2 className="w-5 h-5 text-[#004276]" />
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              <button onClick={() => handleDeleteLab(i)}>
                                <Trash2 className="w-5 h-5 text-red-500" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
                {/* Recommendation Details */}
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-[#003A71] mb-2">
                    Recommendation
                  </h4>
                  <div className="flex items-start gap-2">
                    <textarea
                      value={mainData.recommendation || ""}
                      onChange={(e) =>
                        setMainData((prev) => ({
                          ...prev,
                          recommendation: e.target.value,
                        }))
                      }
                      placeholder="Enter recommendation..."
                      className="flex-1 border border-gray-300 rounded-lg p-3 text-sm resize-none"
                      rows={3}
                    />
                  </div>
                </div>
                {/* Surgery Recommendation Details */}
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-[#003A71] mb-2">
                    Surgery Recommendation
                  </h4>
                  <div className="flex items-start gap-2">
                    <textarea
                      value={mainData.surgeryRecommendation || ""}
                      onChange={(e) =>
                        setMainData((prev) => ({
                          ...prev,
                          surgeryRecommendation: e.target.value,
                        }))
                      }
                      placeholder="Enter surgery recommendation..."
                      className="flex-1 border border-gray-300 rounded-lg p-3 text-sm resize-none"
                      rows={3}
                    />
                  </div>
                </div>
                {/* Follow-up Date Display */}
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-[#003A71] mb-2">
                    Follow-up Date
                  </h4>
                  <div className="flex items-center gap-3">
                    <input
                      type="date"
                      value={mainData.followUpVisitDate || ""}
                      onChange={(e) =>
                        setMainData((prev) => ({
                          ...prev,
                          followUpVisitDate: e.target.value,
                        }))
                      }
                      className="flex-1 border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#004276] focus:border-transparent"
                      min={new Date().toISOString().split('T')[0]} // Prevent selecting past dates
                    />
                    {mainData.followUpVisitDate && (
                      <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                        {new Date(mainData.followUpVisitDate).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Actions */}
            <div className="mt-8 flex justify-end gap-4">
              <button
                onClick={() => setPreviewModalOpen(false)}
                className="py-2 px-6 rounded-lg border border-[#004276] text-[#004276] hover:bg-[#E7F1FF]"
              >
                Cancel
              </button>
              <button
                onClick={uploadToServer}
                className="py-2 px-6 rounded-lg bg-[#004276] text-white hover:bg-[#00305C]"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add/Edit Medicine Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-2 sm:p-0">
          <div className="bg-white p-4 sm:p-6 rounded-lg w-full max-w-lg sm:max-w-6xl shadow-xl overflow-y-auto max-h-[95vh]">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold">
                {editingMedicineIdx !== null ? "Edit Medicine" : "Add Medicine"}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingMedicineIdx(null);
                }}
                className="text-2xl leading-none"
              >
                ×
              </button>
            </div>
            {/* Form Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 items-start">
              {/* Medicine Name */}
              <div className="col-span-1 relative">
                <label className="text-sm font-semibold">Medicine Name</label>
                <input
                  type="text"
                  placeholder="Search/Add Medicine"
                  className="border p-2 rounded w-full"
                  value={newMedicine.name}
                  onChange={(e) => {
                    setIsUserTyping(true);
                    setNewMedicine({ ...newMedicine, name: e.target.value });
                  }}
                />
                {loading ? (
                  <p>Loading...</p>
                ) : searchResultsdata.length > 0 ? (
                  <div
                    style={{
                      padding: "9px 0px 0px 12px",
                      position: "absolute",
                      backgroundColor: "white",
                      width: "100%",
                      border: "1px solid #ccc",
                      maxHeight: "300px",
                      overflowY: "auto",
                      zIndex: 50,
                    }}
                  >
                    <ul>
                      {searchResultsdata.map((item) => (
                        <li key={item.id}>
                          <button
                            onClick={() => handleItemClick(item)}
                            className="w-full text-left hover:bg-gray-100 px-2 py-1"
                          >
                            {item.medicineName}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              {/* Composition */}
              <div className="col-span-1">
                <label className="text-sm font-semibold">Composition</label>
                <input
                  type="text"
                  placeholder="Composition"
                  className="border p-2 rounded w-full text-gray-400"
                  value={newMedicine.composition}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      composition: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-span-1">
                <label className="text-sm font-semibold">Drug Category</label>
                <input
                  type="text"
                  placeholder="Drug Category"
                  className="border p-2 rounded w-full text-gray-400"
                  value={newMedicine.drugCategory}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      drugCategory: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-span-1">
                <label className="text-sm font-semibold">Type of Sell</label>
                <input
                  type="text"
                  placeholder="Type of Sell"
                  className="border p-2 rounded w-full text-gray-400"
                  value={newMedicine.medicineTypeOfSell}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      medicineTypeOfSell: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-span-1">
                <label className="text-sm font-semibold">Rate</label>
                <input
                  type="text"
                  placeholder="Rate"
                  className="border p-2 rounded w-full text-gray-400"
                  value={newMedicine.medicineRate}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      medicineRate: e.target.value,
                    })
                  }
                />
              </div>
              {/* Quantity */}
              <div className="col-span-1">
                <label className="text-sm font-semibold">Quantity</label>
                <input
                  type="number"
                  min={1}
                  className="border p-2 rounded w-full"
                  value={newMedicine.quantity}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      quantity: Number(e.target.value),
                    })
                  }
                />
              </div>
              {/* Dosage */}
              <div className="col-span-1">
                <label className="text-sm font-semibold">Dosage</label>
                <select
                  className="border p-2 rounded w-full"
                  value={newMedicine.dosage}
                  onChange={(e) =>
                    setNewMedicine({ ...newMedicine, dosage: e.target.value })
                  }
                >
                  {dosageOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Instructions */}
              <div className="col-span-1">
                <label className="text-sm font-semibold">Instructions</label>
                <select
                  className="border p-2 rounded w-full"
                  value={newMedicine.instructions}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      instructions: e.target.value,
                    })
                  }
                >
                  <option value="None">None</option>
                  <option value="After Meal">After Meal</option>
                  <option value="Before Meal">Before Meal</option>
                </select>
              </div>

              {/* Route */}
              <div className="col-span-1">
                <label className="text-sm font-semibold">Route</label>
                <select
                  className="border p-2 rounded w-full"
                  value={newMedicine.route}
                  onChange={(e) =>
                    setNewMedicine({
                      ...newMedicine,
                      route: e.target.value,
                    })
                  }
                >
                  <option value="">Select Route</option>
                  <option value="Oral">Oral</option>
                  <option value="Topical">Topical</option>
                  <option value="Intravenous">Intravenous (IV)</option>
                  <option value="Intramuscular">Intramuscular (IM)</option>
                  <option value="Subcutaneous">Subcutaneous</option>
                  <option value="Nasal">Nasal</option>
                  <option value="Inhalation">Inhalation</option>
                </select>
              </div>

              {/* Duration */}
              <div className="col-span-1">
                <label className="text-sm font-semibold">Duration</label>
                <select
                  className="border p-2 rounded w-full"
                  value={newMedicine.duration}
                  onChange={(e) =>
                    setNewMedicine({ ...newMedicine, duration: e.target.value })
                  }
                >
                  <option value="">Select Duration</option>
                  {[...Array(30)].map((_, i) => (
                    <option key={i} value={`${i + 1}`}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Actions */}
            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingMedicineIdx(null);
                }}
                className="text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMedicine}
                className="bg-[#004276] text-white px-6 py-2 rounded"
              >
                {editingMedicineIdx !== null ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add/Edit Lab Modal */}
      {showAddLabModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-0">
          <div className="bg-white rounded-xl w-full max-w-md p-4 sm:p-6 shadow-xl overflow-y-auto max-h-[95vh]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingLabIdx !== null ? "Edit Lab Test" : "Add Lab Test"}
              </h2>
              <button
                onClick={() => {
                  setShowAddLabModal(false);
                  setEditingLabIdx(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-lg"
              >
                ×
              </button>
            </div>
            {/* Test Name */}
            <label className="block text-sm text-gray-600 font-medium mb-1">
              Test Name
            </label>
            <input
              type="text"
              className="border p-2 rounded w-full mb-2"
              value={manualLabTest}
              onChange={searchTests}
              placeholder="Search a test"
            />
            {/* Show suggestions */}
            {filteredOptions.length > 0 && (
              <div className="border border-gray-200 rounded mt-2 max-h-64 overflow-y-auto shadow-sm">
                <ul className="text-sm text-gray-700">
                  {filteredOptions.map((item, index) => (
                    <li
                      key={index}
                      onClick={async () => {
                        setManualLabTest(item.label);
                        setSelectedLabDetails({
                          amount: item.amount || "",
                          code: item.code || "",
                        });
                        setFilteredOptions([]);
                      }}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Selected Test Info */}
            {manualLabTest && (
              <div className="mt-4 text-sm text-gray-600 space-y-2">
                <div>
                  <span className="font-medium">Test Code:</span>{" "}
                  {selectedLabDetails.code || "N/A"}
                </div>
                <div>
                  <span className="font-medium">Amount:</span> ₹
                  {selectedLabDetails.amount || "N/A"}
                </div>
              </div>
            )}
            {/* Actions */}
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => {
                  setShowAddLabModal(false);
                  setEditingLabIdx(null);
                }}
                className="text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLab}
                className="bg-gradient-to-r from-[#004172] to-[#3cba84] text-white px-6 py-2 rounded-full text-sm font-medium"
              >
                {editingLabIdx !== null ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {uploading && <FullScreenLoader message="Uploading Prescription..." />}
    </>
  );
}
